export const MARKDOWN_SOURCE_SIZE_LIMIT_BYTES: number = 5 * 1024 * 1024;

const SUPPORTED_MARKDOWN_EXTENSION = /\.(?:md|markdown|mdown)$/i;
const MINIMUM_BINARY_CONTROL_CHARACTERS = 3;
const BINARY_CONTROL_CHARACTER_RATIO = 0.3;

export type MarkdownValidationErrorCode =
	| 'unsupported_extension'
	| 'file_too_large'
	| 'read_failed'
	| 'invalid_utf8'
	| 'nul_byte'
	| 'binary_content'
	| 'empty_content';

export type MarkdownValidationResult =
	| {
			ok: true;
			file: File;
			text: string;
	  }
	| {
			ok: false;
			errorCode: MarkdownValidationErrorCode;
	  };

const validationError = (errorCode: MarkdownValidationErrorCode): MarkdownValidationResult => ({
	ok: false,
	errorCode,
});

const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onerror = () => reject(reader.error);
		reader.onload = () => {
			if (reader.result instanceof ArrayBuffer) {
				resolve(reader.result);
				return;
			}

			reject(new Error('Markdown file could not be read as binary data'));
		};

		reader.readAsArrayBuffer(file);
	});

const isBinaryText = (text: string): boolean => {
	let controlCharacterCount = 0;

	for (let index = 0; index < text.length; index++) {
		const code = text.charCodeAt(index);
		const isDisallowedControlCharacter =
			(code >= 0x01 && code <= 0x08) ||
			code === 0x0b ||
			code === 0x0c ||
			(code >= 0x0e && code <= 0x1f) ||
			code === 0x7f;

		if (isDisallowedControlCharacter) {
			controlCharacterCount++;
		}
	}

	return (
		controlCharacterCount >= MINIMUM_BINARY_CONTROL_CHARACTERS &&
		controlCharacterCount / text.length >= BINARY_CONTROL_CHARACTER_RATIO
	);
};

export const validateMarkdownFile = async (file: File): Promise<MarkdownValidationResult> => {
	if (!SUPPORTED_MARKDOWN_EXTENSION.test(file.name)) {
		return validationError('unsupported_extension');
	}

	if (file.size > MARKDOWN_SOURCE_SIZE_LIMIT_BYTES) {
		return validationError('file_too_large');
	}

	let bytes: ArrayBuffer;
	try {
		bytes = await readFileAsArrayBuffer(file);
	} catch {
		return validationError('read_failed');
	}

	let text: string;
	try {
		text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
	} catch {
		return validationError('invalid_utf8');
	}

	if (text.includes('\0')) {
		return validationError('nul_byte');
	}

	if (text.trim().length === 0) {
		return validationError('empty_content');
	}

	if (isBinaryText(text)) {
		return validationError('binary_content');
	}

	return { ok: true, file, text };
};
